import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { QueryError } from '@/components/QueryError';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { GRANTABLE_PERMISSION_GROUPS, grantErrorMessage, useCustomRoles } from '@/lib/custom-roles';
import { ROLE_PERMISSIONS, type Permission } from '@/lib/permissions';
import { PERMISSION_LABELS } from '@/lib/role-display';
import { memberName, useBatchOverrides, useOverrides, type Member } from '@/lib/users';

type OverrideState = 'inherit' | 'grant' | 'deny';
type States = Partial<Record<Permission, OverrideState>>;

const OPTIONS: { value: OverrideState; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'grant', label: 'Accordé' },
  { value: 'deny', label: 'Refusé' },
];

interface Props { member: Member | null; onOpenChange: (open: boolean) => void }

/** Grants / denies individuels d'un membre, par-dessus son rôle (custom role, sinon rôle standard). */
export function OverridesDialog({ member, onOpenChange }: Props) {
  const query = useOverrides(member?.membershipId);
  const { data: customRoles } = useCustomRoles(!!member);
  const batch = useBatchOverrides();
  // baseline : état connu du serveur ; states : saisie en cours. Le diff des deux donne le batch.
  const [baseline, setBaseline] = useState<States | null>(null);
  const [states, setStates] = useState<States>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBaseline(null); setError(null); }, [member]);

  const overrides = query.data?.data;
  useEffect(() => {
    if (!overrides || baseline) return;
    const initial: States = Object.fromEntries(overrides.map((o) => [o.permission, o.granted ? 'grant' : 'deny']));
    setBaseline(initial);
    setStates(initial);
  }, [overrides, baseline]);

  if (!member) return null;

  const customRole = customRoles?.data.find((r) => r.id === member.customRoleId);
  const inherited = new Set<string>(customRole?.permissions ?? ROLE_PERMISSIONS[member.role]);
  const stateOf = (s: States | null, p: Permission) => s?.[p] ?? 'inherit';

  const save = async () => {
    if (!baseline) return;
    const changed = GRANTABLE_PERMISSION_GROUPS.flatMap((g) => g.permissions)
      .filter((p) => stateOf(states, p) !== stateOf(baseline, p));
    if (changed.length === 0) { onOpenChange(false); return; }
    setError(null);
    setSaving(true);
    try {
      const changes = changed.map((p) => {
        const next = stateOf(states, p);
        return { permission: p, granted: next === 'inherit' ? null : next === 'grant' };
      });
      await batch.mutateAsync({ membershipId: member.membershipId, changes });
      toast.success(`Permissions de ${memberName(member)} mises à jour`);
      onOpenChange(false);
    } catch (err) {
      // Rien n'a été appliqué : baseline inchangée, la saisie reste à l'écran pour un nouvel essai
      setError(grantErrorMessage(err, 'Erreur lors de l’enregistrement des permissions'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Permissions personnalisées</DialogTitle>
          <DialogDescription>
            {memberName(member)} — « Hérité » suit le rôle {customRole ? `« ${customRole.name} »` : 'du membre'}.
            Un refus l&apos;emporte sur un accord.
          </DialogDescription>
        </DialogHeader>
        <QueryError query={query} />
        {!baseline && !query.isError && <Skeleton className="h-40" />}
        {baseline && (
          <div className="max-h-[55vh] overflow-y-auto space-y-4 pr-1">
            {GRANTABLE_PERMISSION_GROUPS.map((g) => (
              <div key={g.label}>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{g.label}</p>
                {g.permissions.map((p) => (
                  <fieldset key={p} className="flex flex-wrap items-center justify-between gap-2 py-1 border-b last:border-0">
                    <legend className="sr-only">{PERMISSION_LABELS[p]}</legend>
                    <span className="text-sm" aria-hidden>
                      {PERMISSION_LABELS[p]}
                      <span className="ml-2 text-xs text-muted-foreground">
                        (rôle : {inherited.has(p) ? 'oui' : 'non'})
                      </span>
                    </span>
                    <span className="flex gap-3 text-sm">
                      {OPTIONS.map((o) => (
                        <label key={o.value} className="flex items-center gap-1">
                          <input
                            type="radio"
                            name={`override-${p}`}
                            value={o.value}
                            checked={stateOf(states, p) === o.value}
                            onChange={() => setStates((s) => ({ ...s, [p]: o.value }))}
                          />
                          {o.label}
                        </label>
                      ))}
                    </span>
                  </fieldset>
                ))}
              </div>
            ))}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => void save()} disabled={!baseline || saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
